import { useParams } from 'react-router-dom'
import {
	Wallet,
	ArrowUpRight,
	Target,
	UserCheck,
	UserPlus,
	DollarSign,
	ArrowRightLeft,
	Star,
	CircleDot,
	Users,
	Activity,
	BadgeDollarSign,
	Info,
} from 'lucide-react'
import { useQueryChannelTransactionCumulative } from '../../api/hooks/useQueryChannelTransactionCumulative'
import { Card } from '@/common/ui/card'
import { Skeleton } from '@/common/ui/skeleton'
import { DateRangePicker } from '@/common/components/DateRangePicker'
import { useDateFilter } from '@/common/hooks/useDateFilter'
import NavigateBackButton from '@/common/components/NavigateBackButton'
import { Separator } from '@/common/ui/separator'
import Heading from '@/common/shared/heading'
import DateRangeButtons from '@/common/components/DateRangeButtons'
import { TrendingUp, TrendingDown, ArrowDown, ArrowUp } from 'lucide-react'
import {
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from '@/common/ui/tooltip'
import ChannelPlayerTableSection from './ChannelPlayerTable'
import { ChannelCohortTable } from './ChannelCohortTable'

const GAME_TYPE_TRANSLATIONS: Record<string, string> = {
	SLOT: 'สล็อต',
	FISHING: 'เกมยิงปลา',
	SPORT: 'กีฬา',
	LOTTO: 'หวย',
	CASINO: 'คาสิโน',
}

const GAME_TYPE_ICONS: Record<string, string> = {
	SLOT: '🎰',
	FISHING: '🎣',
	SPORT: '⚽',
	LOTTO: '🎱',
	CASINO: '🃏',
}

interface MetricCardProps {
	label: string
	value: string
	info?: string
	icon: React.ElementType
	trend?: 'up' | 'down'
	bgColor?: string
	iconColor?: string
	tooltip?: string
}

function MetricCard({
	label,
	value,
	info,
	icon: Icon,
	trend,
	bgColor = 'bg-blue-50 dark:bg-blue-950',
	iconColor = 'text-blue-600 dark:text-blue-400',
	tooltip,
}: MetricCardProps) {
	return (
		<Card className="overflow-hidden transition-all hover:shadow-lg">
			<div className="p-6">
				<div className="flex items-center justify-between">
					<div className="flex items-center gap-2">
						<div className={`rounded-lg p-2 ${bgColor}`}>
							<Icon className={`h-5 w-5 ${iconColor}`} />
						</div>
						<h3 className="text-sm font-medium text-muted-foreground">
							{label}
						</h3>
						{tooltip && (
							<TooltipProvider>
								<Tooltip>
									<TooltipTrigger asChild>
										<Info className="h-3.5 w-3.5 shrink-0 cursor-help text-muted-foreground/60 hover:text-muted-foreground" />
									</TooltipTrigger>
									<TooltipContent className="max-w-64 text-xs leading-relaxed">
										{tooltip}
									</TooltipContent>
								</Tooltip>
							</TooltipProvider>
						)}
					</div>
					{trend && (
						<div
							className={
								trend === 'up'
									? 'text-green-600 dark:text-green-400'
									: 'text-red-600 dark:text-red-400'
							}
						>
							{trend === 'up' ? (
								<ArrowUp className="h-4 w-4" />
							) : (
								<ArrowDown className="h-4 w-4" />
							)}
						</div>
					)}
				</div>
				<div className="mt-4">
					<p className="select-text text-2xl font-bold text-foreground">
						{value}
					</p>
				</div>
				{info && (
					<div className="mt-2">
						<div className="flex items-center gap-1 text-sm text-muted-foreground">
							<CircleDot className={`h-2 w-2 ${iconColor}`} />
							<span className="select-text">{info}</span>
						</div>
					</div>
				)}
			</div>
		</Card>
	)
}

function MetricCardSkeleton() {
	return (
		<Card className="overflow-hidden">
			<div className="p-6">
				<Skeleton className="mb-4 h-4 w-24" />
				<Skeleton className="h-8 w-32" />
				<Skeleton className="mt-2 h-3 w-40" />
			</div>
		</Card>
	)
}

function SectionLabel({ children }: { children: React.ReactNode }) {
	return (
		<h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
			{children}
		</h3>
	)
}

const ChannelSummary = () => {
	const { channelId } = useParams<{ channelId: string }>()
	const { dateFilter } = useDateFilter()

	const { data, isLoading } = useQueryChannelTransactionCumulative(
		channelId || '',
		{
			startDate: dateFilter.startDate || '',
			endDate: dateFilter.endDate || '',
		},
	)

	const fmt = (n: number) =>
		new Intl.NumberFormat('th-TH', {
			minimumFractionDigits: 0,
			maximumFractionDigits: 0,
		}).format(n)

	const fmtCount = (n: number) => new Intl.NumberFormat('th-TH').format(n)

	if (isLoading) {
		return (
			<div className="space-y-4">
				<div className="flex justify-between">
					<Heading
						title="สรุปช่องทาง"
						description="สรุปผลการดำเนินงานตามช่องทาง"
					/>
					<div className="flex flex-col items-end gap-4">
						<NavigateBackButton />
					</div>
				</div>
				<Separator />
				<div className="space-y-8">
					{[6, 4, 5].map((count, i) => (
						<div key={i} className="space-y-6">
							<Skeleton className="h-5 w-32" />
							<div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
								{Array.from({ length: count }).map((_, j) => (
									<MetricCardSkeleton key={j} />
								))}
							</div>
						</div>
					))}
				</div>
			</div>
		)
	}

	const totalProfit = data?.totalCumulative.totalProfit || 0
	const totalDepositAmount = data?.totalCumulative.totalDepositAmount || 0
	const totalWithdrawAmount = data?.totalCumulative.totalWithdrawAmount || 0
	const totalPlayerCount = data?.totalCumulative.totalPlayerCount || 0
	const totalActivePlayerCount =
		data?.totalCumulative.totalActivePlayerCount || 0

	const newPlayerCount = data?.totalCumulative.newPlayerCount || 0
	const newPlayerDepositCount = data?.totalCumulative.newPlayerDepositCount || 0
	const newActivePlayerCount = data?.totalCumulative.newActivePlayerCount || 0
	const newPlayerTotalDepositAmount =
		data?.totalCumulative.newPlayerTotalDepositAmount || 0
	const firstDepositCount = data?.totalCumulative.firstDepositCount || 0

	const newDepositRate =
		newPlayerCount > 0
			? ((newPlayerDepositCount / newPlayerCount) * 100).toFixed(1)
			: '0.0'

	return (
		<div className="space-y-4">
			{/* Header */}
			<div className="flex justify-between">
				<Heading
					title="สรุปช่องทาง"
					description="สรุปผลการดำเนินงานตามช่องทางในช่วงเวลาที่เลือก"
				/>
				<div className="flex flex-col items-end gap-4">
					<div className="flex items-center gap-2">
						<NavigateBackButton />
						<DateRangePicker />
					</div>
					<DateRangeButtons />
				</div>
			</div>

			<Separator />

			<div className="space-y-8">
				{/* Financial */}
				<div className="space-y-6">
					<SectionLabel>การเงิน</SectionLabel>
					<div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
						<MetricCard
							label="ยอดฝากทั้งหมด"
							value={fmt(totalDepositAmount)}
							info={`${fmtCount(data?.totalCumulative.totalDepositTime || 0)} รายการ`}
							icon={Wallet}
							trend="up"
							bgColor="bg-green-50 dark:bg-green-950"
							iconColor="text-green-600 dark:text-green-400"
						/>
						<MetricCard
							label="ยอดถอนทั้งหมด"
							value={fmt(totalWithdrawAmount)}
							info={`${fmtCount(data?.totalCumulative.totalWithdrawTime || 0)} รายการ`}
							icon={DollarSign}
							trend="down"
							bgColor="bg-red-50 dark:bg-red-950"
							iconColor="text-red-600 dark:text-red-400"
						/>
						<MetricCard
							label="กำไรสุทธิ"
							value={new Intl.NumberFormat('th-TH', {
								minimumFractionDigits: 0,
								maximumFractionDigits: 0,
								signDisplay: 'auto',
							}).format(totalProfit)}
							info={totalProfit >= 0 ? 'กำไร' : 'ขาดทุน'}
							icon={totalProfit >= 0 ? TrendingUp : TrendingDown}
							bgColor="bg-blue-50 dark:bg-blue-950"
							iconColor="text-blue-600 dark:text-blue-400"
						/>
						<MetricCard
							label="เทิร์นโอเวอร์"
							value={fmt(data?.totalCumulative.totalTurnoverAmount || 0)}
							icon={Target}
							bgColor="bg-indigo-50 dark:bg-indigo-950"
							iconColor="text-indigo-600 dark:text-indigo-400"
						/>
						<MetricCard
							label="โอนเข้าระบบ"
							value={fmt(data?.totalCumulative.totalTransferIn || 0)}
							icon={ArrowUpRight}
							bgColor="bg-emerald-50 dark:bg-emerald-950"
							iconColor="text-emerald-600 dark:text-emerald-400"
						/>
						<MetricCard
							label="โอนออกจากระบบ"
							value={fmt(data?.totalCumulative.totalTransferOut || 0)}
							icon={ArrowRightLeft}
							bgColor="bg-rose-50 dark:bg-rose-950"
							iconColor="text-rose-600 dark:text-rose-400"
						/>
					</div>
				</div>

				{/* All-time players */}
				<div className="space-y-6">
					<SectionLabel>ผู้เล่นทั้งหมด (ทุกช่วงเวลา)</SectionLabel>
					<div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
						<MetricCard
							label="จำนวนผู้เล่นทั้งหมด"
							value={fmtCount(totalPlayerCount)}
							info="ลงทะเบียนผ่านช่องทางนี้"
							icon={Users}
							bgColor="bg-purple-50 dark:bg-purple-950"
							iconColor="text-purple-600 dark:text-purple-400"
						/>
						<MetricCard
							label="ผู้เล่นที่มีการใช้งาน"
							value={fmtCount(totalActivePlayerCount)}
							info={`${totalPlayerCount > 0 ? ((totalActivePlayerCount / totalPlayerCount) * 100).toFixed(1) : '0.0'}% ของผู้เล่นทั้งหมด`}
							icon={UserCheck}
							bgColor="bg-violet-50 dark:bg-violet-950"
							iconColor="text-violet-600 dark:text-violet-400"
						/>
						<MetricCard
							label="ผู้เล่นที่ฝากครั้งแรก"
							value={fmtCount(firstDepositCount)}
							info="ฝากครั้งแรกในช่วงนี้"
							icon={Star}
							bgColor="bg-amber-50 dark:bg-amber-950"
							iconColor="text-amber-600 dark:text-amber-400"
						/>
					</div>
				</div>

				{/* New player funnel */}
				<div className="space-y-6">
					<SectionLabel>ผู้เล่นใหม่ (ในช่วงเวลาที่เลือก)</SectionLabel>
					<div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
						<MetricCard
							label="ผู้เล่นใหม่"
							value={fmtCount(newPlayerCount)}
							info="ลงทะเบียนในช่วงนี้"
							icon={UserPlus}
							bgColor="bg-teal-50 dark:bg-teal-950"
							iconColor="text-teal-600 dark:text-teal-400"
							tooltip="จำนวนผู้เล่นที่สมัครสมาชิกผ่านช่องทางนี้ภายในช่วงเวลาที่เลือก (ไม่ใช่ผู้เล่นทั้งหมดของช่องทาง)"
						/>
						<MetricCard
							label="ผู้เล่นใหม่ที่มีการฝาก"
							value={fmtCount(newPlayerDepositCount)}
							info={`${newDepositRate}% ของผู้เล่นใหม่`}
							icon={ArrowUpRight}
							bgColor="bg-cyan-50 dark:bg-cyan-950"
							iconColor="text-cyan-600 dark:text-cyan-400"
							tooltip="จากผู้เล่นใหม่ในช่วงนี้ มีกี่คนที่ฝากเงินอย่างน้อย 1 ครั้ง"
						/>
						<MetricCard
							label="ผู้เล่นใหม่ที่มีการใช้งาน"
							value={fmtCount(newActivePlayerCount)}
							info={`${newPlayerCount > 0 ? ((newActivePlayerCount / newPlayerCount) * 100).toFixed(1) : '0.0'}% ของผู้เล่นใหม่`}
							icon={Activity}
							bgColor="bg-sky-50 dark:bg-sky-950"
							iconColor="text-sky-600 dark:text-sky-400"
							tooltip="จากผู้เล่นใหม่ในช่วงนี้ มีกี่คนที่มีการเล่นเกมหรือฝากเงินอย่างน้อย 1 ครั้ง (รวมผู้ที่ได้รับโบนัสและเล่นเกมโดยไม่ได้ฝากด้วยตัวเอง)"
						/>
						<MetricCard
							label="ยอดฝากรวมของผู้เล่นใหม่"
							value={fmt(newPlayerTotalDepositAmount)}
							info={`จากผู้เล่น ${fmtCount(newPlayerDepositCount)} คน`}
							icon={BadgeDollarSign}
							trend="up"
							bgColor="bg-teal-50 dark:bg-teal-950"
							iconColor="text-teal-600 dark:text-teal-400"
							tooltip="ยอดฝากทั้งหมดของผู้เล่นใหม่ที่สมัครในช่วงนี้ — ใช้ประเมินคุณภาพและมูลค่าของกลุ่มผู้เล่นใหม่จากช่องทางนี้"
						/>
					</div>
				</div>

				{/* Game Types */}
				{data?.gameTypeCumulative && (
					<div className="space-y-6">
						<SectionLabel>สรุปตามประเภทเกม</SectionLabel>
						<div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
							{Object.entries(data.gameTypeCumulative).map(
								([gameType, stats]) => {
									const netTransfer =
										stats.totalTransferIn - stats.totalTransferOut
									return (
										<Card
											key={gameType}
											className="overflow-hidden transition-all hover:shadow-lg"
										>
											<div className="p-6">
												<div className="flex items-center gap-2">
													<span className="text-xl">
														{GAME_TYPE_ICONS[gameType] || '🎮'}
													</span>
													<h3 className="text-sm font-medium text-muted-foreground">
														{GAME_TYPE_TRANSLATIONS[gameType] || gameType}
													</h3>
												</div>
												<div className="mt-4 space-y-3">
													<div>
														<p className="text-xs text-muted-foreground">
															เทิร์นโอเวอร์
														</p>
														<p className="select-text text-2xl font-bold text-foreground">
															{fmt(stats.totalTurnoverAmount)}
														</p>
													</div>
													<div className="grid grid-cols-2 gap-2 border-t pt-3">
														<div>
															<p className="text-xs text-muted-foreground">
																โอนเข้า
															</p>
															<p className="select-text text-sm font-semibold text-green-600 dark:text-green-400">
																{fmt(stats.totalTransferIn)}
															</p>
														</div>
														<div>
															<p className="text-xs text-muted-foreground">
																โอนออก
															</p>
															<p className="select-text text-sm font-semibold text-red-600 dark:text-red-400">
																{fmt(stats.totalTransferOut)}
															</p>
														</div>
													</div>
													<div className="flex items-center gap-1 text-sm text-muted-foreground">
														<CircleDot
															className={`h-2 w-2 ${netTransfer >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}
														/>
														<span className="select-text">
															สุทธิ{' '}
															<span
																className={
																	netTransfer >= 0
																		? 'font-semibold text-green-600 dark:text-green-400'
																		: 'font-semibold text-red-600 dark:text-red-400'
																}
															>
																{new Intl.NumberFormat('th-TH', {
																	minimumFractionDigits: 0,
																	maximumFractionDigits: 0,
																	signDisplay: 'always',
																}).format(netTransfer)}
															</span>
														</span>
													</div>
												</div>
											</div>
										</Card>
									)
								},
							)}
						</div>
					</div>
				)}

				{/* Cohort analytics table */}
				{channelId && (
					<div className="space-y-4">
						<SectionLabel>สถิติตามช่วงเวลา</SectionLabel>
						<ChannelCohortTable channelId={channelId} />
					</div>
				)}

				{/* Player list */}
				{channelId && (
					<div className="space-y-4">
						<SectionLabel>รายชื่อผู้เล่นทั้งหมด (ทุกช่วงเวลา)</SectionLabel>
						<ChannelPlayerTableSection channelId={channelId} />
					</div>
				)}
			</div>
		</div>
	)
}

export default ChannelSummary
